"use client";

import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowRightLeft,
  BookOpen,
  ChevronDown,
  Blocks,
  Hand,
  Link2,
  Phone,
  Route,
  UserCircle,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import {
  AGENT_TEMPLATES_REQUEST_ROLE_HREF,
  AGENT_TEMPLATES_SEE_ALL_HREF,
  CASE_STUDIES_INDUSTRIES,
  CASE_STUDIES_ROLES,
  getProductMegaItems,
  PRODUCT_MEGA_CUSTOM_DEV_HREF,
  PRODUCT_MEGA_SEE_ALL_HREF,
  SITEGLUE_PLATFORM_MEGA_SEE_ALL_HREF,
  SITEGLUE_PRODUCT_MEGA_GROUP_LABELS,
  RESOURCES_MEGA_LINKS,
  type MarketingMegaLink,
  type ProductMegaGroup,
  type ProductMegaIcon,
  type ProductMegaItem,
} from "~/lib/marketplace/marketing-nav-config";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import {
  SITEGLUE_INTEGRATIONS_NAV,
  SITEGLUE_INTEGRATIONS_NAV_LABEL,
  SITEGLUE_INTEGRATIONS_SEE_ALL_HREF,
} from "~/lib/siteglue/siteglue-integrations-nav";
import { SITEGLUE_PAGE_STORY } from "~/lib/siteglue/page-story";
import { HITL_TERMS } from "~/lib/siteglue/hitl-terminology";
import {
  SITEGLUE_AI_EMPLOYEES_BROWSE_INDUSTRY_HREF,
  SITEGLUE_AI_EMPLOYEES_BROWSE_ROLE_HREF,
  SITEGLUE_AI_EMPLOYEES_LINE,
  SITEGLUE_AI_EMPLOYEES_REQUEST_HREF,
  SITEGLUE_AI_EMPLOYEES_SEE_ALL_HREF,
  SITEGLUE_FEATURED_AI_EMPLOYEES,
  SITEGLUE_FEATURED_ROLES_LABEL,
  SITEGLUE_ROLES_MEGA_TITLE,
  SITEGLUE_ROLES_NAV_LABEL,
  SITEGLUE_ROLES_REQUEST_LABEL,
  SITEGLUE_ROLES_SEE_ALL_LABEL,
} from "~/lib/siteglue/ai-employees-showcase";
import {
  SITEGLUE_INDUSTRIES_MEGA_LINE,
  SITEGLUE_INDUSTRIES_MEGA_TITLE,
  SITEGLUE_INDUSTRIES_NAV,
  SITEGLUE_INDUSTRIES_NAV_LABEL,
  SITEGLUE_INDUSTRIES_SEE_ALL_HREF,
} from "~/lib/siteglue/siteglue-industries-nav";
import {
  SITEGLUE_SOLUTION_CATEGORIES,
  SITEGLUE_SOLUTIONS_MEGA_LINE,
  SITEGLUE_SOLUTIONS_MEGA_TITLE,
  SITEGLUE_SOLUTIONS_NAV_LABEL,
  SITEGLUE_SOLUTIONS_SEE_ALL_HREF,
  SITEGLUE_SOLUTIONS_BASE_PATH,
  SITEGLUE_SOLUTION_PRODUCT_LINKS,
  getSiteGlueSolutionsByCategory,
  solutionHref,
} from "~/lib/siteglue/siteglue-solutions-nav";
import { cn } from "~/lib/utils";

type MegaMenuId =
  | "product"
  | "agent-templates"
  | "solutions"
  | "industries"
  | "resources"
  | "about"
  | "integrations";

type MegaMenuTriggerProps = {
  label: string;
  active: boolean;
  onToggle: () => void;
  testId: string;
};

function MegaMenuTrigger({ label, active, onToggle, testId }: MegaMenuTriggerProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onToggle}
      aria-expanded={active}
      className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "bg-primary/10 text-primary"
          : "text-primary hover:bg-primary/8 hover:text-primary/80"
      }`}
    >
      {label}
      <ChevronDown
        className={`h-3.5 w-3.5 transition-transform duration-200 ${active ? "rotate-180" : ""}`}
      />
    </button>
  );
}

function MobileNavLink({
  href,
  label,
  description,
  onNavigate,
  testId,
  variant = "default",
}: {
  href: string;
  label: string;
  description?: string;
  onNavigate: () => void;
  testId?: string;
  variant?: "default" | "emphasis" | "muted";
}) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      data-testid={testId}
      className={cn(
        "block rounded-lg px-3 py-2.5 transition-colors active:bg-muted/80",
        variant === "emphasis" && "text-sm font-semibold text-foreground hover:bg-muted/70",
        variant === "muted" && "text-sm font-medium text-muted-foreground hover:bg-muted/70 hover:text-foreground",
        variant === "default" && "text-sm font-medium text-foreground hover:bg-muted/70",
      )}
    >
      <span className="block">{label}</span>
      {description ? (
        <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{description}</span>
      ) : null}
    </Link>
  );
}

function MobileNavAccordion({
  label,
  summary,
  defaultOpen = false,
  children,
}: {
  label: string;
  summary?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-border/70">
      <button
        type="button"
        className="flex min-h-12 w-full items-center justify-between gap-3 px-1 py-3 text-left"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-foreground">{label}</span>
          {summary ? (
            <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
              {summary}
            </span>
          ) : null}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
            open && "rotate-180 text-primary",
          )}
          aria-hidden
        />
      </button>
      {open ? <div className="space-y-0.5 pb-3">{children}</div> : null}
    </div>
  );
}

function MobileNavNestedAccordion({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-border/60 bg-muted/20">
      <button
        type="button"
        className="flex min-h-10 w-full items-center justify-between gap-2 px-3 py-2 text-left"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {open ? <div className="space-y-0.5 border-t border-border/60 px-1 pb-2 pt-1">{children}</div> : null}
    </div>
  );
}

const PRODUCT_ICONS: Record<ProductMegaIcon, LucideIcon> = {
  hitl: Hand,
  handoff: ArrowRightLeft,
  takeover: Users,
  phoneRules: Phone,
  howItWorks: Route,
  profile: UserCircle,
  responsibilities: Wrench,
  training: BookOpen,
  tools: Link2,
  integrations: Blocks,
};

function ProductMegaLink({
  item,
  onClose,
}: {
  item: ProductMegaItem;
  onClose: () => void;
}) {
  const Icon = PRODUCT_ICONS[item.icon];
  return (
    <Link
      href={item.href}
      onClick={onClose}
      className="group flex gap-3 rounded-xl p-3 transition-colors hover:bg-muted/70"
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground group-hover:border-primary/30 group-hover:text-primary">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{item.label}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
          {item.description}
        </span>
      </span>
    </Link>
  );
}

function groupProductItems(items: ProductMegaItem[]): {
  journey: ProductMegaItem[];
  collaboration: ProductMegaItem[];
  platform: ProductMegaItem[];
  ungrouped: ProductMegaItem[];
} {
  const journey: ProductMegaItem[] = [];
  const collaboration: ProductMegaItem[] = [];
  const platform: ProductMegaItem[] = [];
  const ungrouped: ProductMegaItem[] = [];
  for (const item of items) {
    if (item.group === "journey") journey.push(item);
    else if (item.group === "collaboration") collaboration.push(item);
    else if (item.group === "platform") platform.push(item);
    else ungrouped.push(item);
  }
  return { journey, collaboration, platform, ungrouped };
}

type MegaMenuBrandId = "siteglue" | "inbound_gpt";

function resolveMegaMenuIsSiteGlue(brandId?: MegaMenuBrandId): boolean {
  if (brandId) return brandId === "siteglue";
  return getMarketingChrome().brandId === "siteglue";
}

/** Product mega menu — Profile, Responsibilities, Training, Tools, Integrations. */
export function ProductMegaMenu({
  brandId,
  open,
  onToggle,
  onClose,
}: {
  /** Force SiteGlue labels when host is app.easygpt.builders (e.g. `/onboard`). */
  brandId?: MegaMenuBrandId;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const isSiteGlue = resolveMegaMenuIsSiteGlue(brandId);
  const productItems = getProductMegaItems(isSiteGlue ? "siteglue" : undefined);
  const megaLabel = isSiteGlue ? SITEGLUE_PAGE_STORY.platform.navLabel : "Product";
  const seeAllHref = isSiteGlue
    ? SITEGLUE_PLATFORM_MEGA_SEE_ALL_HREF
    : PRODUCT_MEGA_SEE_ALL_HREF;
  const seeAllLabel = isSiteGlue ? "Explore the Platform →" : "See all features →";
  const grouped = groupProductItems(productItems);

  return (
    <div className="relative hidden md:block">
      <MegaMenuTrigger
        label={megaLabel}
        active={open}
        onToggle={onToggle}
        testId="link-product"
      />
      {open ? (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={onClose} />
          <div
            className="absolute left-0 top-full z-50 mt-2 w-[min(100vw-2rem,48rem)] rounded-2xl border border-border bg-background p-6 shadow-xl"
            role="menu"
          >
            {isSiteGlue ? (
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {HITL_TERMS.hitlAlways}
              </p>
            ) : null}

            {isSiteGlue ? (
              <div className="space-y-5">
                {grouped.journey.length ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {grouped.journey.map((item) => (
                      <ProductMegaLink key={item.href} item={item} onClose={onClose} />
                    ))}
                  </div>
                ) : null}
                {(
                  [
                    ["collaboration", grouped.collaboration],
                    ["platform", grouped.platform],
                  ] as const
                ).map(([group, items]) =>
                  items.length ? (
                    <div key={group}>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        {SITEGLUE_PRODUCT_MEGA_GROUP_LABELS[group as Exclude<ProductMegaGroup, "journey">]}
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {items.map((item) => (
                          <ProductMegaLink key={item.href} item={item} onClose={onClose} />
                        ))}
                      </div>
                    </div>
                  ) : null,
                )}
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {productItems.map((item) => (
                  <ProductMegaLink key={item.href} item={item} onClose={onClose} />
                ))}
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-4">
              <Link
                href={seeAllHref}
                onClick={onClose}
                className="text-sm font-medium text-primary hover:underline"
              >
                {seeAllLabel}
              </Link>
              {isSiteGlue ? (
                <Link
                  href="/human-in-the-loop"
                  onClick={onClose}
                  className="text-sm font-medium text-muted-foreground hover:text-primary hover:underline"
                >
                  How Humans and AI Work Together →
                </Link>
              ) : (
                <Link
                  href={PRODUCT_MEGA_CUSTOM_DEV_HREF}
                  onClick={onClose}
                  className="text-sm font-medium text-muted-foreground hover:text-primary hover:underline"
                >
                  Custom Agent Development →
                </Link>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Agent Templates / AI Employees mega menu. */
export function AgentTemplatesMegaMenu({
  brandId,
  open,
  onToggle,
  onClose,
}: {
  brandId?: MegaMenuBrandId;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const isSiteGlue = resolveMegaMenuIsSiteGlue(brandId);

  if (isSiteGlue) {
    return (
      <div className="relative hidden md:block">
        <MegaMenuTrigger
          label={SITEGLUE_ROLES_NAV_LABEL}
          active={open}
          onToggle={onToggle}
          testId="link-agent-templates"
        />
        {open ? (
          <>
            <div className="fixed inset-0 z-40" aria-hidden onClick={onClose} />
            <div
              className="absolute left-0 top-full z-50 mt-2 w-[min(100vw-2rem,28rem)] rounded-2xl border border-border bg-background p-6 shadow-xl"
              role="menu"
            >
              <p className="text-sm font-semibold text-foreground">{SITEGLUE_ROLES_MEGA_TITLE}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {SITEGLUE_AI_EMPLOYEES_LINE}
              </p>

              <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {SITEGLUE_FEATURED_ROLES_LABEL}
              </p>
              <ul className="mt-3 space-y-2.5">
                {SITEGLUE_FEATURED_AI_EMPLOYEES.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={onClose}
                      className="block rounded-lg p-2 transition-colors hover:bg-muted/70"
                    >
                      <span className="block text-sm font-semibold text-foreground">
                        {item.label}
                      </span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                        {item.description}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex flex-col gap-2 border-t border-border pt-4">
                <Link
                  href={SITEGLUE_AI_EMPLOYEES_BROWSE_INDUSTRY_HREF}
                  onClick={onClose}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Browse by Industry →
                </Link>
                <Link
                  href={SITEGLUE_AI_EMPLOYEES_BROWSE_ROLE_HREF}
                  onClick={onClose}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Browse by Role →
                </Link>
                <Link
                  href={SITEGLUE_AI_EMPLOYEES_REQUEST_HREF}
                  onClick={onClose}
                  className="text-sm font-medium text-muted-foreground hover:text-primary hover:underline"
                >
                  {SITEGLUE_ROLES_REQUEST_LABEL} →
                </Link>
                <Link
                  href={SITEGLUE_AI_EMPLOYEES_SEE_ALL_HREF}
                  onClick={onClose}
                  className="text-sm font-semibold text-foreground hover:text-primary hover:underline"
                >
                  {SITEGLUE_ROLES_SEE_ALL_LABEL} →
                </Link>
              </div>
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative hidden md:block">
      <MegaMenuTrigger
        label="Agent Templates"
        active={open}
        onToggle={onToggle}
        testId="link-agent-templates"
      />
      {open ? (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={onClose} />
          <div
            className="absolute left-0 top-full z-50 mt-2 w-[min(100vw-2rem,40rem)] rounded-xl border border-border bg-background p-6 shadow-xl"
            role="menu"
          >
            <div className="grid gap-8 sm:grid-cols-2 sm:gap-12">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Industries
                </p>
                <ul className="mt-4 grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-1">
                  {CASE_STUDIES_INDUSTRIES.map((item) => (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        onClick={onClose}
                        className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  href={AGENT_TEMPLATES_SEE_ALL_HREF}
                  onClick={onClose}
                  className="mt-6 inline-flex text-sm font-medium text-primary hover:underline"
                >
                  See all agent templates →
                </Link>
              </div>

              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Roles
                </p>
                <ul className="mt-4 space-y-3">
                  {CASE_STUDIES_ROLES.map((item) => (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        onClick={onClose}
                        className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  href={AGENT_TEMPLATES_REQUEST_ROLE_HREF}
                  onClick={onClose}
                  className="mt-6 inline-flex text-sm font-medium text-primary hover:underline"
                >
                  Request a Role →
                </Link>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** @deprecated Use AgentTemplatesMegaMenu */
export const CaseStudiesMegaMenu = AgentTemplatesMegaMenu;
/** @deprecated Use AgentTemplatesMegaMenu */
export const AgentMarketplaceMegaMenu = AgentTemplatesMegaMenu;

/** Resources mega menu. */
export function ResourcesMegaMenu({
  open,
  onToggle,
  onClose,
  links,
  hrefFor,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  links?: MarketingMegaLink[];
  hrefFor?: (path: string) => string;
}) {
  const items = links ?? RESOURCES_MEGA_LINKS;
  const resolveHref = hrefFor ?? ((path: string) => path);
  return (
    <div className="relative hidden md:block">
      <MegaMenuTrigger
        label="Resources"
        active={open}
        onToggle={onToggle}
        testId="link-resources"
      />
      {open ? (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={onClose} />
          <div
            className="absolute left-0 top-full z-50 mt-2 w-[min(100vw-2rem,28rem)] rounded-xl border border-border bg-background p-4 shadow-xl"
            role="menu"
          >
            <ul className="space-y-1">
              {items.map((item) => (
                <li key={item.href}>
                  <Link
                    href={resolveHref(item.href)}
                    onClick={onClose}
                    className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/70"
                  >
                    <span className="block text-sm font-semibold text-foreground">
                      {item.label}
                    </span>
                    {item.description ? (
                      <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                        {item.description}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              href={resolveHref("/resources")}
              onClick={onClose}
              className="mt-3 block rounded-lg px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/8"
            >
              All resources
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function IntegrationsMegaMenu({
  open,
  onToggle,
  onClose,
  hrefFor,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  hrefFor?: (path: string) => string;
}) {
  const resolveHref = hrefFor ?? ((path: string) => path);
  return (
    <div className="relative hidden md:block">
      <MegaMenuTrigger
        label={SITEGLUE_INTEGRATIONS_NAV_LABEL}
        active={open}
        onToggle={onToggle}
        testId="link-nav-integrations"
      />
      {open ? (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={onClose} />
          <div
            className="absolute left-0 top-full z-50 mt-2 w-[min(100vw-2rem,52rem)] rounded-2xl border border-border bg-background p-6 shadow-xl"
            role="menu"
          >
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {SITEGLUE_INTEGRATIONS_NAV.map((section) => (
                <div key={section.id}>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {section.label}
                  </p>
                  <ul className="mt-3 space-y-2">
                    {section.items.map((item) => (
                      <li key={item.id}>
                        <Link
                          href={resolveHref(item.href)}
                          onClick={onClose}
                          className="block rounded-lg p-2.5 transition-colors hover:bg-muted/70"
                          data-testid={`link-integration-${item.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">{item.label}</span>
                            {item.status === "request" ? (
                              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                Request
                              </span>
                            ) : null}
                          </span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                            {item.description}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <Link
              href={resolveHref(SITEGLUE_INTEGRATIONS_SEE_ALL_HREF)}
              onClick={onClose}
              className="mt-5 block rounded-lg px-2 py-2 text-sm font-semibold text-primary hover:bg-primary/8"
            >
              All integrations
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function MobileIntegrationsSection({
  onNavigate,
  hrefFor,
}: {
  onNavigate: () => void;
  hrefFor?: (path: string) => string;
}) {
  const resolveHref = hrefFor ?? ((path: string) => path);
  return (
    <div className="space-y-3 border-b border-border/70 py-2">
      <p className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {SITEGLUE_INTEGRATIONS_NAV_LABEL}
      </p>
      {SITEGLUE_INTEGRATIONS_NAV.map((section) => (
        <div key={section.id} className="space-y-1">
          <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {section.label}
          </p>
          {section.items.map((item) => (
            <Link
              key={item.id}
              href={resolveHref(item.href)}
              onClick={onNavigate}
              className="flex min-h-11 items-center justify-between rounded-md px-1 text-sm font-semibold text-foreground hover:bg-muted/70"
            >
              <span>{item.label}</span>
              {item.status === "request" ? (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Request
                </span>
              ) : null}
            </Link>
          ))}
        </div>
      ))}
    </div>
  );
}

const SOLUTIONS_MEGA_LEFT_CATEGORY_IDS = [
  "revenue-growth",
  "marketing-growth",
  "automation-outcomes",
] as const;
const SOLUTIONS_MEGA_RIGHT_CATEGORY_IDS = [
  "customer-experience",
  "operational-efficiency",
] as const;

function SolutionsCategoryColumn({
  categoryIds,
  onClose,
}: {
  categoryIds: readonly (typeof SOLUTIONS_MEGA_LEFT_CATEGORY_IDS)[number][];
  onClose: () => void;
}) {
  return (
    <div className="space-y-6">
      {categoryIds.map((categoryId) => {
        const category = SITEGLUE_SOLUTION_CATEGORIES.find((item) => item.id === categoryId);
        if (!category) return null;

        return (
          <div key={category.id}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {category.label}
            </p>
            <ul className="mt-3 space-y-2">
              {getSiteGlueSolutionsByCategory(category.id).map((outcome) => (
                <li key={outcome.slug}>
                  <Link
                    href={solutionHref(outcome.slug)}
                    onClick={onClose}
                    className="block rounded-lg p-2.5 transition-colors hover:bg-muted/70"
                    data-testid={`link-solution-${outcome.slug}`}
                  >
                    <span className="block text-sm font-semibold text-foreground">
                      {outcome.title}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                      {outcome.navDescription}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/** SiteGlue Solutions mega menu — business outcome landing pages. */
export function SolutionsMegaMenu({
  open,
  onToggle,
  onClose,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  return (
    <div className="relative hidden md:block">
      <MegaMenuTrigger
        label={SITEGLUE_SOLUTIONS_NAV_LABEL}
        active={open}
        onToggle={onToggle}
        testId="link-nav-solutions"
      />
      {open ? (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={onClose} />
          <div
            className="absolute left-0 top-full z-50 mt-2 w-[min(100vw-2rem,48rem)] rounded-2xl border border-border bg-background p-6 shadow-xl"
            role="menu"
          >
            <p className="text-sm font-semibold text-foreground">{SITEGLUE_SOLUTIONS_MEGA_TITLE}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {SITEGLUE_SOLUTIONS_MEGA_LINE}
            </p>

            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {SITEGLUE_SOLUTION_PRODUCT_LINKS.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={onClose}
                    className="block rounded-xl px-3 py-2 hover:bg-muted"
                  >
                    <span className="block text-sm font-semibold text-foreground">{item.title}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                      {item.description}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-5 grid max-h-[min(70vh,36rem)] gap-8 overflow-y-auto pr-1 sm:grid-cols-2">
              <SolutionsCategoryColumn
                categoryIds={SOLUTIONS_MEGA_LEFT_CATEGORY_IDS}
                onClose={onClose}
              />
              <SolutionsCategoryColumn
                categoryIds={SOLUTIONS_MEGA_RIGHT_CATEGORY_IDS}
                onClose={onClose}
              />
            </div>

            <div className="mt-5 flex flex-col gap-2 border-t border-border pt-4">
              <Link
                href={SITEGLUE_SOLUTIONS_SEE_ALL_HREF}
                onClick={onClose}
                className="text-sm font-semibold text-foreground hover:text-primary hover:underline"
              >
                How onboarding works →
              </Link>
              <Link
                href={SITEGLUE_AI_EMPLOYEES_BROWSE_INDUSTRY_HREF}
                onClick={onClose}
                className="text-sm font-medium text-primary hover:underline"
              >
                Browse by Industry →
              </Link>
              <Link
                href={SITEGLUE_AI_EMPLOYEES_BROWSE_ROLE_HREF}
                onClick={onClose}
                className="text-sm font-medium text-primary hover:underline"
              >
                Browse by Role →
              </Link>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function MobileSolutionsSection({ onNavigate }: { onNavigate: () => void }) {
  return (
    <MobileNavAccordion label={SITEGLUE_SOLUTIONS_NAV_LABEL} summary={SITEGLUE_SOLUTIONS_MEGA_LINE}>
      {SITEGLUE_SOLUTION_PRODUCT_LINKS.map((item) => (
        <MobileNavLink
          key={item.href}
          href={item.href}
          label={item.title}
          description={item.description}
          onNavigate={onNavigate}
          testId={`mobile-link-solution-product-${item.href.replace(/\//g, "")}`}
          variant="emphasis"
        />
      ))}
      <MobileNavLink
        href={SITEGLUE_SOLUTIONS_BASE_PATH}
        label="Browse all business outcomes"
        description={SITEGLUE_SOLUTIONS_MEGA_TITLE}
        onNavigate={onNavigate}
        testId="mobile-link-solutions-index"
        variant="emphasis"
      />
      <div className="space-y-2 px-1 pt-2">
        {SITEGLUE_SOLUTION_CATEGORIES.map((category) => (
          <MobileNavNestedAccordion key={category.id} label={category.label}>
            {getSiteGlueSolutionsByCategory(category.id).map((outcome) => (
              <MobileNavLink
                key={outcome.slug}
                href={solutionHref(outcome.slug)}
                label={outcome.title}
                description={outcome.navDescription}
                onNavigate={onNavigate}
                testId={`mobile-link-solution-${outcome.slug}`}
                variant="muted"
              />
            ))}
          </MobileNavNestedAccordion>
        ))}
      </div>
      <MobileNavLink
        href={SITEGLUE_SOLUTIONS_SEE_ALL_HREF}
        label="How onboarding works"
        onNavigate={onNavigate}
        variant="emphasis"
      />
    </MobileNavAccordion>
  );
}

/** SiteGlue Industries mega menu — dedicated landing page per industry. */
export function IndustriesMegaMenu({
  open,
  onToggle,
  onClose,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  return (
    <div className="relative hidden md:block">
      <MegaMenuTrigger
        label={SITEGLUE_INDUSTRIES_NAV_LABEL}
        active={open}
        onToggle={onToggle}
        testId="link-nav-industries"
      />
      {open ? (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={onClose} />
          <div
            className="absolute left-0 top-full z-50 mt-2 w-[min(100vw-2rem,40rem)] rounded-2xl border border-border bg-background p-6 shadow-xl"
            role="menu"
          >
            <p className="text-sm font-semibold text-foreground">{SITEGLUE_INDUSTRIES_MEGA_TITLE}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {SITEGLUE_INDUSTRIES_MEGA_LINE}
            </p>
            <ul className="mt-5 grid gap-2 sm:grid-cols-2">
              {SITEGLUE_INDUSTRIES_NAV.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={onClose}
                    className="block rounded-lg p-2.5 transition-colors hover:bg-muted/70"
                    data-testid={`link-industry-${item.href.replace(/\//g, "")}`}
                  >
                    <span className="block text-sm font-semibold text-foreground">
                      {item.navLabel}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                      {item.description}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-col gap-2 border-t border-border pt-4">
              <Link
                href={SITEGLUE_INDUSTRIES_SEE_ALL_HREF}
                onClick={onClose}
                className="text-sm font-semibold text-foreground hover:text-primary hover:underline"
              >
                See all AI Employees →
              </Link>
              <Link
                href={SITEGLUE_AI_EMPLOYEES_BROWSE_ROLE_HREF}
                onClick={onClose}
                className="text-sm font-medium text-primary hover:underline"
              >
                Browse by Role →
              </Link>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function MobileIndustriesSection({ onNavigate }: { onNavigate: () => void }) {
  return (
    <MobileNavAccordion label={SITEGLUE_INDUSTRIES_NAV_LABEL} summary={SITEGLUE_INDUSTRIES_MEGA_LINE}>
      {SITEGLUE_INDUSTRIES_NAV.map((item) => (
        <MobileNavLink
          key={item.href}
          href={item.href}
          label={item.navLabel}
          description={item.description}
          onNavigate={onNavigate}
          testId={`mobile-link-industry-${item.href.replace(/\//g, "")}`}
        />
      ))}
      <MobileNavLink
        href={SITEGLUE_INDUSTRIES_SEE_ALL_HREF}
        label="See all AI Employees"
        onNavigate={onNavigate}
        variant="emphasis"
      />
      <MobileNavLink
        href={SITEGLUE_AI_EMPLOYEES_BROWSE_ROLE_HREF}
        label="Browse by Role"
        onNavigate={onNavigate}
        variant="muted"
      />
    </MobileNavAccordion>
  );
}

/** Close mega menus when clicking outside the nav bar cluster. */
export function AboutMegaMenu({
  open,
  onToggle,
  onClose,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  return (
    <div className="relative">
      <MegaMenuTrigger
        label={SITEGLUE_PAGE_STORY.about.navLabel}
        active={open}
        onToggle={onToggle}
        testId="button-nav-about-menu"
      />
      {open ? (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-xl border border-border bg-background p-2 shadow-lg">
          <Link
            href={SITEGLUE_PAGE_STORY.about.href}
            className="block rounded-lg px-3 py-2.5 transition hover:bg-muted"
            data-testid="link-nav-about-jim"
            onClick={onClose}
          >
            <p className="text-sm font-semibold text-foreground">About Jim Wanek</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{SITEGLUE_PAGE_STORY.about.line}</p>
          </Link>
          <Link
            href={SITEGLUE_PAGE_STORY.myTeam.href}
            className="mt-1 block rounded-lg px-3 py-2.5 transition hover:bg-muted"
            data-testid="link-nav-my-team"
            onClick={onClose}
          >
            <p className="text-sm font-semibold text-foreground">{SITEGLUE_PAGE_STORY.myTeam.navLabel}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{SITEGLUE_PAGE_STORY.myTeam.line}</p>
          </Link>
        </div>
      ) : null}
    </div>
  );
}

export function MobileAboutSection({ onNavigate }: { onNavigate: () => void }) {
  return (
    <MobileNavAccordion label={SITEGLUE_PAGE_STORY.about.navLabel} summary={SITEGLUE_PAGE_STORY.about.line}>
      <MobileNavLink
        href={SITEGLUE_PAGE_STORY.about.href}
        label="About Jim Wanek"
        description={SITEGLUE_PAGE_STORY.about.line}
        onNavigate={onNavigate}
        testId="mobile-link-about-jim"
      />
      <MobileNavLink
        href={SITEGLUE_PAGE_STORY.myTeam.href}
        label={SITEGLUE_PAGE_STORY.myTeam.navLabel}
        description={SITEGLUE_PAGE_STORY.myTeam.line}
        onNavigate={onNavigate}
        testId="mobile-link-my-team"
      />
    </MobileNavAccordion>
  );
}

/** Close mega menus when clicking outside the nav bar cluster. */
export function useMegaMenuState() {
  const [openMenu, setOpenMenu] = useState<MegaMenuId | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return {
    navRef,
    openMenu,
    setOpenMenu,
    closeMenus: () => setOpenMenu(null),
  };
}

export function MobileProductSection({
  brandId,
  onNavigate,
}: {
  brandId?: MegaMenuBrandId;
  onNavigate: () => void;
}) {
  const isSiteGlue = resolveMegaMenuIsSiteGlue(brandId);
  const productItems = getProductMegaItems(isSiteGlue ? "siteglue" : undefined);
  const seeAllHref = isSiteGlue
    ? SITEGLUE_PLATFORM_MEGA_SEE_ALL_HREF
    : PRODUCT_MEGA_SEE_ALL_HREF;
  const seeAllLabel = isSiteGlue ? "Explore the Platform" : "See all features";
  const platformLabel = isSiteGlue ? SITEGLUE_PAGE_STORY.platform.navLabel : "Product";

  return (
    <MobileNavAccordion
      label={platformLabel}
      summary={isSiteGlue ? HITL_TERMS.hitlAlways : undefined}
    >
      {productItems.map((item) => (
        <MobileNavLink
          key={item.href}
          href={item.href}
          label={item.label}
          description={item.description}
          onNavigate={onNavigate}
        />
      ))}
      <MobileNavLink href={seeAllHref} label={seeAllLabel} onNavigate={onNavigate} variant="emphasis" />
      {isSiteGlue ? (
        <MobileNavLink
          href="/human-in-the-loop"
          label="How Humans and AI Work Together"
          onNavigate={onNavigate}
          variant="muted"
        />
      ) : (
        <MobileNavLink
          href={PRODUCT_MEGA_CUSTOM_DEV_HREF}
          label="Custom Agent Development"
          onNavigate={onNavigate}
          variant="muted"
        />
      )}
    </MobileNavAccordion>
  );
}

export function MobileAgentTemplatesSection({
  brandId,
  onNavigate,
}: {
  brandId?: MegaMenuBrandId;
  onNavigate: () => void;
}) {
  const isSiteGlue = resolveMegaMenuIsSiteGlue(brandId);

  if (isSiteGlue) {
    return (
      <MobileNavAccordion label={SITEGLUE_ROLES_NAV_LABEL} summary={SITEGLUE_AI_EMPLOYEES_LINE}>
        {SITEGLUE_FEATURED_AI_EMPLOYEES.map((item) => (
          <MobileNavLink
            key={item.href}
            href={item.href}
            label={item.label}
            description={item.description}
            onNavigate={onNavigate}
          />
        ))}
        <MobileNavLink
          href={SITEGLUE_AI_EMPLOYEES_BROWSE_INDUSTRY_HREF}
          label="Browse by Industry"
          onNavigate={onNavigate}
          variant="muted"
        />
        <MobileNavLink
          href={SITEGLUE_AI_EMPLOYEES_BROWSE_ROLE_HREF}
          label="Browse by Role"
          onNavigate={onNavigate}
          variant="muted"
        />
        <MobileNavLink
          href={SITEGLUE_AI_EMPLOYEES_REQUEST_HREF}
          label={SITEGLUE_ROLES_REQUEST_LABEL}
          onNavigate={onNavigate}
          variant="emphasis"
        />
        <MobileNavLink
          href={SITEGLUE_AI_EMPLOYEES_SEE_ALL_HREF}
          label={SITEGLUE_ROLES_SEE_ALL_LABEL}
          onNavigate={onNavigate}
          variant="emphasis"
        />
      </MobileNavAccordion>
    );
  }

  return (
    <MobileNavAccordion label="Agent Templates">
      <MobileNavNestedAccordion label="Industries">
        {CASE_STUDIES_INDUSTRIES.map((item) => (
          <MobileNavLink
            key={item.href}
            href={item.href}
            label={item.label}
            onNavigate={onNavigate}
            variant="muted"
          />
        ))}
      </MobileNavNestedAccordion>
      <MobileNavNestedAccordion label="Roles">
        {CASE_STUDIES_ROLES.map((item) => (
          <MobileNavLink
            key={item.href}
            href={item.href}
            label={item.label}
            onNavigate={onNavigate}
            variant="muted"
          />
        ))}
      </MobileNavNestedAccordion>
      <MobileNavLink
        href={AGENT_TEMPLATES_SEE_ALL_HREF}
        label="See all agent templates"
        onNavigate={onNavigate}
        variant="emphasis"
      />
      <MobileNavLink
        href={AGENT_TEMPLATES_REQUEST_ROLE_HREF}
        label="Request a Role"
        onNavigate={onNavigate}
        variant="muted"
      />
    </MobileNavAccordion>
  );
}

/** @deprecated Use MobileAgentTemplatesSection */
export const MobileCaseStudiesSection = MobileAgentTemplatesSection;
/** @deprecated Use MobileAgentTemplatesSection */
export const MobileMarketplaceSection = MobileAgentTemplatesSection;

export function MobileResourcesSection({
  onNavigate,
  links,
  hrefFor,
}: {
  onNavigate: () => void;
  links?: MarketingMegaLink[];
  hrefFor?: (path: string) => string;
}) {
  const items = links ?? RESOURCES_MEGA_LINKS;
  const resolveHref = hrefFor ?? ((path: string) => path);
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <p className="px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Resources
      </p>
      {items.map((item) => (
        <Link
          key={item.href}
          href={resolveHref(item.href)}
          onClick={onNavigate}
          className="block rounded-md px-3 py-2 text-sm font-medium text-primary hover:bg-primary/8"
        >
          {item.label}
        </Link>
      ))}
      <Link
        href={resolveHref("/resources")}
        onClick={onNavigate}
        className="block rounded-md px-3 py-2 text-sm font-semibold text-foreground hover:bg-muted/70"
      >
        All resources
      </Link>
    </div>
  );
}
